import { useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";

import { useSelector } from "react-redux";
import { dispatch, getState } from "../../../../state/store";
import {
  setIsNeedRefetchLocales,
  setLocalizationToApp,
  setPrevLocale
} from "../../../../state/ducks";
import {
  selectOriginalDefaultLocale,
  selectSelectedAppLocalizations
} from "../../../../state/selectors";

import { strCode } from "../../../../consts";
import { appsService, notificationService, t } from "../../../../services";
import { responseUtil } from "../../../../utils";

import { Checkbox, FlexContainers, WrapperContainer } from "../../../../components";
import { useIsLocalisationEditing } from "../hooks/useIsLocalisationEditing";

export const DefaultQuestionCell = ({ cell, getEditingLocalization }) => {
  const selectedApp = useSelector((store) => store.apps.selectedApp);

  const initialCellValue = useRef(cell.row.original);

  const localizations = useMemo(() => {
    return selectedApp.localizations;
  }, [selectedApp]);

  const { isEdit, isLocalizationEditing } = useIsLocalisationEditing({
    originalRow: cell.row.original,
    getEditingLocalization
  });

  useEffect(() => {
    if (!getEditingLocalization()) {
      initialCellValue.current = cell.row.original;
    }
  }, [getEditingLocalization]);

  const onDefaultSelecting = async (event) => {
    event.stopPropagation();

    if (cell.row.original.isDefault) {
      return;
    }

    const name = cell.row.original.name;
    const appLocalizations = selectedApp.localizations;
    const prevLocale = appLocalizations.find((locale) => locale.isDefault);

    dispatch(setPrevLocale(prevLocale));

    const localizationData = appLocalizations.map((localization, index) => ({
      ...localization,
      isDefault: localization.name === name && cell.row.index === index
    }));

    dispatch(setLocalizationToApp(localizationData));

    const isEmptyLocale = localizations.find((locale) => !locale.name || !locale.path);

    if (!cell.row.original.name || isEmptyLocale || (isLocalizationEditing && isEdit)) {
      return;
    }

    const data = localizationData.map(({ name, isDefault }) => ({
      name,
      isDefault
    }));

    const res = await appsService.updateApp({
      name: selectedApp.name,
      appType: selectedApp.appType,
      localizations: data
    });

    if (responseUtil.isResponseSuccessful(res)) {
      notificationService.showSuccess({
        title: t(strCode.APP_UPDATED_SHORT_TITLTE),
        message: t(strCode.APP_UPDATED_LONG_TITLTE, { name: selectedApp.name }),
        immediate: false
      });

      dispatch(setIsNeedRefetchLocales(true));
    } else {
      const appLocalizations = selectSelectedAppLocalizations()(getState());
      const defaultLocle = selectOriginalDefaultLocale()(getState());

      const savedLocalizations = [...appLocalizations].map((localization) => {
        return {
          ...localization,
          isDefault: localization.name === defaultLocle.name
        };
      });

      dispatch(setLocalizationToApp(savedLocalizations));
    }
  };

  return (
    <>
      <WrapperContainer
        Container={FlexContainers.FlexRowStart}
        ml="16"
        pr="24px"
        width="100%"
        gap={8}>
        <WrapperContainer mr={16}>
          {(!isEdit ? true : cell.row.original.isDefault || isLocalizationEditing) ? (
            <Checkbox
              checked={cell.row.original.isDefault}
              disabled={localizations.length === 1}
              onCheck={onDefaultSelecting}
            />
          ) : (
            <WrapperContainer width={"24px"} height={"24px"} />
          )}
        </WrapperContainer>
      </WrapperContainer>
    </>
  );
};

DefaultQuestionCell.propTypes = {
  cell: PropTypes.shape({
    value: PropTypes.string.isRequired,
    row: PropTypes.shape({
      index: PropTypes.number.isRequired,
      original: PropTypes.shape({
        name: PropTypes.string.isRequired,
        isDefault: PropTypes.bool.isRequired
      }).isRequired
    }).isRequired
  }).isRequired,
  getEditingLocalization: PropTypes.func.isRequired
};
